/* eslint-disable react/prop-types */

import React, { PureComponent } from 'react'

import _ from 'lodash'

import Tabs, { Tab } from '@stackhouse/dashboard-components/Tabs'
import { generatePath } from '@stackhouse/dashboard-components/Dashboard'

import GridPage from './grid'
import FilterPage from './filter'
import DetailPage from './detail'
import AttachmentPage from './attachment'
import FilterGridPage from './filter-grid'
import ReportFilterPage from './report-filter'
import ReportPage from './report'

import withPermissions from '../actions'

const templates = {
    'grid': GridPage,
    'filter': FilterPage,
    'detail': DetailPage,
    'attachment': AttachmentPage,
    'filter-grid': FilterGridPage,
    'report-filter': ReportFilterPage,
    'report': ReportPage
}

/**
 * @param {object}  page                - Page configuration
 * @param {string}  page.formName       - Page form name (used to fetch permissions).
 * @param {string}  page.recordParamKey - Param key identifying main resource id.
 * @param {array}   page.paramsKeys     - List of accessory paras keys to use for permissions fetching.
 * @param {array}   page.tabs           - List of tab object with format { main: bool, id: string, template: string, actions: array }.
 * @param {array}   page.actions        - List of action objects.
 */
const createPage = ({ formName, recordParamKey, paramsKeys = [], tabs = [], actions = [] }) => {

    const Page = class extends PureComponent {

        constructor(props) {
            super(props)

            const { match: { params: { [recordParamKey]: functionRecordId } = {} } = {} } = props
            
            this.formName = formName
            this.paramsKeys = paramsKeys
            this.recordParamKey = recordParamKey
            this.resourceId = +functionRecordId

            tabs.forEach(({ id, title }) => {
                this[`${id || _.camelCase(title)}Ref`] = React.createRef()
            })

            const { match: { params }, location: { state } } = props

            const pageParams = Object.entries(params).reduce((acc, [key, value]) => {
                const resultKey = Array.isArray(paramsKeys) ? paramsKeys.includes(key) ? key : null : paramsKeys[key]

                if (resultKey) {
                    return { ...acc, [key]: isFinite(value) ? +value : value }
                }

                return key
            }, { [recordParamKey]: params[recordParamKey] })

            this.pageParams = pageParams
            this.initialData = (state && state.data) || null
        }


        renderTab = (tab) => {
            const { initialData } = this
            const { editable, actions, match: { url } } = this.props

            const { title, id, template, Component, ...contentProps } = tab

            const tabName = id || _.camelCase(title)
            const Content = template ? templates[template] : Component

            const setRef = (ref) => {
                const refKey = `${tabName}Ref`
                this[refKey].current = ref

                const { forwardedMethods } = this[refKey].current?.constructor || {}
                if (forwardedMethods) {
                    forwardedMethods.forEach(key => this[key] = ref[key])
                }
            }

            const interpolatedContentProps = Object.entries(contentProps).reduce((acc, [key, value]) => {

                if (typeof value == 'string') {
                    return { ...acc, [key]: generatePath(value.replace(':url', url), this.pageParams) }
                }

                return { ...acc, [key]: value }
            }, {})

            return (
                <Tab title={title} key={title} disabled={!tab.main && !this.resourceId}>
                    {Content ? (
                        <Content
                            ref={setRef}
                            title={title}
                            editable={editable}
                            data={initialData}
                            formName={formName}
                            params={this.pageParams}
                            resourceId={this.resourceId}
                            {...interpolatedContentProps}
                            actions={(actions && actions.children) ? actions.children[tabName] : undefined}
                        />
                    ) : null}
                </Tab>
            )
        }

        render() {
            const { view } = this.props

            if (tabs.length === 1) return this.renderTab(tabs[0])

            const visibleTabs = tabs.filter(Boolean).filter(tab => tab.main || (view || {})[tab.id || _.camelCase(tab.title)])
            if (!visibleTabs.length) return null

            return (
                <Tabs fill strategy={'defer'} contentContainerProps={{ overflow: 'auto' }}>
                    {visibleTabs.map(this.renderTab)}
                </Tabs>
            )
        }

    }

    const childrenActions = tabs.reduce((acc, { id, title, actions: tabActions }) => {
        if (tabActions && Array.isArray(tabActions) && tabActions.length)
            return { ...acc, [`${id || _.camelCase(title)}`]: tabActions }

        return acc
    }, {})

    const childrenList = tabs.reduce((acc, { id, main, title }) => {
        if (main) return acc
        return [...acc, `${id || _.camelCase(title)}`]
    }, [])


    return withPermissions(Page, formName, recordParamKey, actions, childrenActions, childrenList)
}

export { createPage, templates, createPage as default }